import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ObjectCategory } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Eye, Folder, GripVertical, MoreHorizontal, Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useDeleteObjectCategory,
  useObjectCategories,
  useUpdateObjectCategoryOrder,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableActiveFilterTags,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { cn } from '@/shared/utils';
import { getObjectCategoryIcon } from '@/shared/utils/object-category-icons';
import { isGmnAdminGroup } from '@/shared/utils/permissions';
import { getSortParam } from '@/shared/utils/table-utils';

const ActionCell = ({
  category,
  onDelete,
  onViewDetail,
  isGmnAdmin,
}: {
  category: ObjectCategory;
  onDelete: (category: ObjectCategory) => void;
  onViewDetail: (id: string) => void;
  isGmnAdmin: boolean;
}) => {
  const { t } = useIntl();
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onViewDetail(category.id);
        }}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        aria-label={t('common.view')}
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
            aria-label={t('navigation.openMenu')}
            title={t('navigation.openMenu')}
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => navigate(`/gmn/settings/object-categories/${category.id}`)}
          >
            {t('common.edit')}
          </DropdownMenuItem>
          {isGmnAdmin && (
            <DropdownMenuItem
              onClick={() => {
                window.setTimeout(() => onDelete(category), 0);
              }}
            >
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const EMPTY_CATEGORIES: ObjectCategory[] = [];

const areSameCategoryOrder = (left: ObjectCategory[], right: ObjectCategory[]) => {
  if (left.length !== right.length) return false;
  return left.every((item, index) => item.id === right[index]?.id);
};

type SortableRowContextValue = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
  isDragging: boolean;
  isReorderEnabled: boolean;
  rowId: string;
};

const SortableRowContext = createContext<SortableRowContextValue | null>(null);

const DragHandleCell = ({ label }: { label: string }) => {
  const context = useContext(SortableRowContext);
  if (!context) return null;

  const { attributes, listeners, setActivatorNodeRef, isReorderEnabled, rowId } = context;

  return (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={label}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded border border-transparent text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isReorderEnabled ? 'cursor-grab hover:text-foreground' : 'cursor-not-allowed opacity-40'
      )}
      onClick={(event) => event.stopPropagation()}
      disabled={!isReorderEnabled}
      data-testid={`object-category-drag-${rowId}`}
    >
      <GripVertical size={14} />
    </button>
  );
};

type SortableCategoryRowProps = {
  row: Row<ObjectCategory>;
  onViewDetail: (id: string) => void;
  isReorderEnabled: boolean;
};

function SortableCategoryRow({ row, onViewDetail, isReorderEnabled }: SortableCategoryRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.original.id,
    disabled: !isReorderEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const visibleCells = row.getVisibleCells();

  return (
    <SortableRowContext.Provider
      value={{
        attributes,
        listeners,
        setActivatorNodeRef,
        isDragging,
        isReorderEnabled,
        rowId: row.original.id,
      }}
    >
      <TableRow
        ref={setNodeRef}
        style={style}
        data-state={row.getIsSelected() && 'selected'}
        data-testid={`object-category-row-${row.original.id}`}
        className={cn('cursor-pointer', isDragging && 'bg-muted/60')}
        onClick={() => {
          if (isDragging) return;
          onViewDetail(row.original.id);
        }}
      >
        {visibleCells.map((cell, index) => (
          <TableCell
            key={cell.id}
            style={{ width: `${cell.column.getSize()}px` }}
            className={cn(
              index === 0 ? 'pl-6' : '',
              index === visibleCells.length - 1 ? 'pr-6' : ''
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    </SortableRowContext.Provider>
  );
}

export function ObjectCategoriesOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = isGmnAdminGroup(currentUser);

  const tablePreferences = useTablePreferences({
    tableId: 'object-categories-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  const [categoryToDelete, setCategoryToDelete] = useState<ObjectCategory | null>(null);
  const deleteCategory = useDeleteObjectCategory();
  const updateCategoryOrder = useUpdateObjectCategoryOrder();
  const [orderedCategories, setOrderedCategories] = useState<ObjectCategory[]>(EMPTY_CATEGORIES);
  const [isReordering, setIsReordering] = useState(false);
  const [pendingOrderIds, setPendingOrderIds] = useState<string[] | null>(null);

  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeFilterTags = searchTerm
    ? [
        {
          id: 'search',
          label: (
            <>
              <span className="font-medium text-foreground">{t('common.searchLabel')}:</span>
              <span className="truncate max-w-[240px]" title={searchTerm}>
                {searchTerm}
              </span>
            </>
          ),
          onRemove: () => handleSearchChange(''),
        },
      ]
    : [];

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`/gmn/settings/object-categories/${id}`);
    },
    [navigate]
  );

  const handleDelete = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory.mutateAsync(categoryToDelete.id);
        toast.success(t('objectCategories.categoryDeletedSuccessfully'));
        setCategoryToDelete(null);
      } catch (_error) {
        toast.error(t('objectCategories.errorDeletingCategory'));
      }
    }
  };

  const moveLabel = t('common.move');

  const columns: ColumnDef<ObjectCategory>[] = [
    {
      id: 'sortOrder',
      header: () => <span className="sr-only">{t('objectCategories.sortOrder')}</span>,
      cell: () => <DragHandleCell label={moveLabel} />,
      enableSorting: false,
      size: 44,
      minSize: 40,
      maxSize: 48,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => {
        const Icon = getObjectCategoryIcon(row.original.icon);
        return (
          <div className="flex items-center gap-2">
            <Icon size={16} className="text-primary" />
            <span className="font-medium text-foreground">{row.getValue('name')}</span>
          </div>
        );
      },
      size: 250,
      minSize: 150,
      maxSize: 400,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <SortableHeader column={column} title={t('common.description')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue('description') || '-'}</span>
      ),
      size: 400,
      minSize: 200,
      maxSize: 600,
    },
    {
      id: 'metadataFields',
      header: t('objectCategories.metadataFields'),
      cell: ({ row }) => {
        const schema = row.original.metadataSchema as {
          properties?: Record<string, unknown>;
        } | null;
        const fieldCount = schema?.properties ? Object.keys(schema.properties).length : 0;
        return <span className="text-foreground">{fieldCount}</span>;
      },
      size: 120,
      minSize: 80,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          category={row.original}
          onDelete={setCategoryToDelete}
          onViewDetail={handleViewDetail}
          isGmnAdmin={isGmnAdmin}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: categoriesData, isLoading } = useObjectCategories({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where: debouncedSearchTerm ? { name: { like: debouncedSearchTerm } } : undefined,
    sort: getSortParam<ObjectCategory>(sorting) ?? 'sortOrder',
  });

  const categories = categoriesData?.docs ?? EMPTY_CATEGORIES;
  const totalPages = categoriesData?.totalPages || 0;
  const totalDocs = categoriesData?.totalDocs || 0;

  useEffect(() => {
    if (isReordering) {
      if (pendingOrderIds && categories.length === pendingOrderIds.length) {
        const matchesPending = categories.every(
          (category, index) => category.id === pendingOrderIds[index]
        );
        if (matchesPending) {
          setOrderedCategories(categories);
          setPendingOrderIds(null);
          setIsReordering(false);
        }
      }
      return;
    }

    if (!areSameCategoryOrder(orderedCategories, categories)) {
      setOrderedCategories(categories);
    }
  }, [categories, isReordering, orderedCategories, pendingOrderIds]);

  const isReorderEnabled =
    !isLoading &&
    !debouncedSearchTerm &&
    sorting.length === 0 &&
    !updateCategoryOrder.isPending &&
    !isReordering &&
    orderedCategories.length > 1;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!isReorderEnabled) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedCategories.findIndex((category) => category.id === active.id);
      const newIndex = orderedCategories.findIndex((category) => category.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextCategories = arrayMove(orderedCategories, oldIndex, newIndex);
      const previousCategories = orderedCategories;
      setIsReordering(true);
      setOrderedCategories(nextCategories);
      setPendingOrderIds(nextCategories.map((category) => category.id));

      const pageOffset = pagination.pageIndex * pagination.pageSize;
      const updates = nextCategories.map((category, index) => ({
        id: category.id,
        sortOrder: pageOffset + index + 1,
      }));

      updateCategoryOrder.mutate(updates, {
        onSuccess: () => {
          toast.success(t('objectCategories.orderUpdatedSuccessfully'));
        },
        onError: () => {
          setOrderedCategories(previousCategories);
          setPendingOrderIds(null);
          setIsReordering(false);
          toast.error(t('objectCategories.errorUpdatingOrder'));
        },
      });
    },
    [
      isReorderEnabled,
      orderedCategories,
      pagination.pageIndex,
      pagination.pageSize,
      t,
      updateCategoryOrder,
    ]
  );

  const table = useReactTable({
    data: orderedCategories,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <PageContainer>
      <PageTitle
        icon={Folder}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>
              {t('navigation.items.gmnObjectCategories')}
            </SimpleBreadcrumbItem>
          </>
        }
        title={t('objectCategories.objectCategories')}
        primaryAction={{
          label: t('objectCategories.addCategory'),
          onClick: () => navigate('/gmn/settings/object-categories/new'),
          icon: <Plus size={16} />,
          disabled: !isGmnAdmin,
        }}
      />

      <TableCard className="mb-4">
        <TableCardHeader>
          <TableSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t('objectCategories.searchPlaceholder')}
          />
        </TableCardHeader>
      </TableCard>

      {activeFilterTags.length > 0 ? (
        <div className="mb-4">
          <TableActiveFilterTags tags={activeFilterTags} />
        </div>
      ) : null}

      <TableCard>
        <TableCardContent>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={
                        index === 0
                          ? 'pl-6'
                          : index === headerGroup.headers.length - 1
                            ? 'pr-6 text-right'
                            : ''
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableHeaderRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={table.getRowModel().rows.map((row) => row.original.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {table.getRowModel().rows.map((row) => (
                      <SortableCategoryRow
                        key={row.id}
                        row={row}
                        onViewDetail={handleViewDetail}
                        isReorderEnabled={isReorderEnabled}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : !isLoading ? (
                <EmptyTableRow colSpan={columns.length} />
              ) : (
                <TableLoadingCell colSpan={columns.length} />
              )}
            </TableBody>
          </Table>
        </TableCardContent>

        <TableCardFooter>
          <TablePagination table={table} />
        </TableCardFooter>
      </TableCard>

      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('objectCategories.deleteCategory')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('objectCategories.deleteCategoryConfirmation', {
                name: categoryToDelete?.name || t('objectCategories.category'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteCategory.isPending}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
